/**
 * @fileoverview Transaction Provider Component
 * Provides manual transaction handling and transaction details modal across the app
 */

import React, { createContext, useContext, ReactNode } from 'react';
import {
	useManualTransactions,
	type TransactionConfirmedCallback
} from '../../hooks/useManualTransactions';
import { useZcashWallet, useZcashTransaction } from '../../hooks/useZcashWallet';
import TransactionDetailsModal from './TransactionDetailsModal';
import type { TransactionData } from '../../services/zcashWallet';

/**
 * Extra knobs for the in-browser (winbit32) signer. Ignored on the manual
 * copy-paste / seed-phrase path.
 */
export interface SendTransactionOptions {
	/** Spend transparent UTXOs to top up an insufficient shielded balance. */
	allowTransparentInputs?: boolean;
	/** Skip the Orchard note scan and shield from transparent immediately. */
	skipShieldedScan?: boolean;
	/** Cancel an in-flight send (the note scan) — used by the mint shortcut. */
	abortSignal?: AbortSignal;
}

interface TransactionContextType {
	// Manual transaction handling
	handleManualTransaction: (
		txData: TransactionData,
		description: string,
		onConfirmed?: TransactionConfirmedCallback
	) => void;
	isManualWallet: boolean;

	// Wrapper functions that handle both automatic and manual transactions.
	// `onConfirmed` fires after the broadcast is confirmed (auto-sign path)
	// or after the user clicks "I've copied" in the manual modal, allowing
	// callers to chain a follow-up API call such as `recordListing` /
	// `recordTransfer` / `recordPurchase`.
	sendNFPTTransaction: (
		recipient: string,
		memo: string,
		amount?: number,
		onConfirmed?: TransactionConfirmedCallback,
		options?: SendTransactionOptions
	) => Promise<void>;
	sendPaymentTransaction: (
		recipient: string,
		amount: number,
		memo?: string,
		onConfirmed?: TransactionConfirmedCallback,
		options?: SendTransactionOptions
	) => Promise<void>;

	// Transaction state
	isTransacting: boolean;
	transactionError: string | null;
}

const TransactionContext = createContext<TransactionContextType | undefined>(undefined);

interface TransactionProviderProps {
	children: ReactNode;
}

export const TransactionProvider: React.FC<TransactionProviderProps> = ({ children }) => {
	const { connection, isConnected } = useZcashWallet();
	const {
		sendNFPTTransaction: sendAutomaticNFPTTransaction,
		sendPaymentTransaction: sendAutomaticPaymentTransaction,
		isCreatingTransaction,
		transactionError,
	} = useZcashTransaction();
	const {
		showTransactionDetails,
		transactionDetails,
		closeTransactionDetailsModal,
		handleManualTransaction,
		confirmTransactionSent
	} = useManualTransactions();

	// "Manual" means: we don't have an in-browser shielded wallet ready to
	// sign for the user, so we just hand them the memo+address+amount and
	// let them either copy-paste into any Zcash wallet or paste a seed
	// phrase into the inline auto-send panel. This is also the right path
	// for visitors who never picked a signing option at all — the user
	// isn't asked to "connect" anything before they can mint.
	const requiresManualFlow = !isConnected || connection?.walletType === 'manual';

	const sendNFPTTransaction = async (
		recipient: string,
		memo: string,
		amount: number = 0.0001,
		onConfirmed?: TransactionConfirmedCallback,
		options?: SendTransactionOptions
	): Promise<void> => {
		if (requiresManualFlow) {
			handleManualTransaction(
				{ recipient, amount, memo, fee: 0.0001 },
				'Send this shielded inscription from any Zcash wallet — copy the details below, or paste your seed phrase to broadcast in-browser.',
				onConfirmed
			);
			return;
		}
		// Auto-sign path: surface the broadcast txid to the callback so
		// callers can record the mint/listing optimistically and link to the
		// explorer. (The manual copy-paste flow has no txid here; its modal
		// reports the txid via its own success handler instead.)
		const signed = await sendAutomaticNFPTTransaction(recipient, memo, amount, options);
		try {
			await onConfirmed?.(signed?.txid);
		} catch (err) {
			console.error('onConfirmed callback failed:', err);
		}
	};

	const sendPaymentTransaction = async (
		recipient: string,
		amount: number,
		memo?: string,
		onConfirmed?: TransactionConfirmedCallback,
		options?: SendTransactionOptions
	): Promise<void> => {
		if (requiresManualFlow) {
			handleManualTransaction(
				{ recipient, amount, memo: memo || '', fee: 0.0001 },
				'Send this shielded payment from any Zcash wallet — copy the details below, or paste your seed phrase to broadcast in-browser.',
				onConfirmed
			);
			return;
		}
		const signed = await sendAutomaticPaymentTransaction(recipient, amount, memo, options);
		try {
			await onConfirmed?.(signed?.txid);
		} catch (err) {
			console.error('onConfirmed callback failed:', err);
		}
	};

	const contextValue: TransactionContextType = {
		handleManualTransaction,
		// Exposed so callers can still tell whether they're in
		// copy-paste-or-seed-phrase mode vs an in-browser signer.
		isManualWallet: requiresManualFlow,
		sendNFPTTransaction,
		sendPaymentTransaction,
		isTransacting: isCreatingTransaction,
		transactionError: transactionError || null,
	};

	return (
		<TransactionContext.Provider value={contextValue}>
			{children}
			
			{/* Transaction Details Modal */}
			<TransactionDetailsModal
				isOpen={showTransactionDetails}
				onClose={closeTransactionDetailsModal}
				transactionDetails={transactionDetails}
				// Manual "I've copied" — no txid available.
				onConfirmCopied={() => confirmTransactionSent()}
				// Auto-sign success — txid known from WebZjs broadcast result.
				onAutoSendSuccess={(txid) => confirmTransactionSent(txid)}
			/>
		</TransactionContext.Provider>
	);
};

// Hook to use transaction context
export const useTransactionContext = (): TransactionContextType => {
	const context = useContext(TransactionContext);
	if (!context) {
		throw new Error('useTransactionContext must be used within a TransactionProvider');
	}
	return context;
};

export default TransactionProvider; 